import React, { useEffect, useState } from 'react';
import Button from '../../components/Button/Button';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { tranportFormValidationSchema } from '../../utils/formValidation';
import useFormValidation from '../../hooks/useFormValidation';
import { toast } from 'react-toastify';
import { errorToast } from '../../utils/errorHandling';
import { AiOutlineLoading } from 'react-icons/ai';
import { getTransportForm, getTransportSettings, updateTransportForm } from '../../services/transportationApi';
import dayjs from 'dayjs';
import utc from "dayjs/plugin/utc";
import { convertToDateTimeInputFormat, convertToUTCDateTimeInputFormat, convertToUTCDateTimeRangeFormat } from '../../utils/utility';
import SubmissionConfirmationPage from '../../components/SubmissionConfirmationPage';

const base_url = process.env.REACT_APP_BASE_URL

const ViewTransportation = () => {
    dayjs.extend(utc);
    const { t } = useTranslation();
    const [formSection, setFormSection] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [arriveDateError, setArriveDateError] = useState(null);
    const [departDateError, setDepartDateError] = useState(null);
    const [formData, setFormData] = useState("");
    const [originalData, setOriginalData] = useState(null);
    const [formPercentage, setFormPercentage] = useState("33%");
    const { errors, validateField, validateAllFields } = useFormValidation(tranportFormValidationSchema(t));
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formValidationSchema, setFormValidationSchema] = useState();
    const lang = localStorage.getItem("i18nextLng");
    const [transportationSetting, setTransportationSetting] = useState(null);
    const [courseData, setCourseData] = useState(null);

    const [previews, setPreviews] = useState({});
    const [fileError, setFileError] = useState(null);
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    const handleFileChange = (event) => {
        const { id } = event.target;
        const selectedFile = event.target.files[0];
        const allowedTypes = [
            "image/jpeg",
            "image/png",
            "application/pdf",
            "image/jpg",
            "image/heic"
        ];
        if (selectedFile && !allowedTypes.includes(selectedFile.type)) {
            setFileError((prevState) => ({
                ...prevState,
                [id]: t("FormValid.FileExtensionAllow"),
            }));
            event.target.value = ""; // Clear the file input
            setPreviews((prev) => ({ ...prev, [id]: null }));
            return;
        } else if (selectedFile && selectedFile.size > maxSize) {
            setFileError((prevState) => ({
                ...prevState,
                [id]: t("FormValid.smallerFile10MB"),
            }));
            event.target.value = ""; // Clear the file input
            setPreviews((prev) => ({ ...prev, [id]: null }));
            return;
        }
        setFileError((prevState) => ({
            ...prevState,
            [id]: null,
        }));

        setFormData((prevState) => ({
            ...prevState,
            [id]: selectedFile,
        }));

        if (selectedFile && selectedFile.type.startsWith("image/")) {
            const previewUrl = URL.createObjectURL(selectedFile);
            setPreviews((prev) => ({
                ...prev,
                [id]: previewUrl,
            }));
        } else {
            // If PDF or non-image → no preview
            setPreviews((prev) => ({
                ...prev,
                [id]: null,
            }));
        }

        validateField(id, selectedFile);

    };
    const fetchTransport = async () => {
        try {
            const res = await getTransportForm();
            if (res) {
                var data = res.data;
                data.depart_datetime = data.depart_datetime && convertToUTCDateTimeInputFormat(data.depart_datetime);
                data.arrive_datetime = data.arrive_datetime && convertToUTCDateTimeInputFormat(data.arrive_datetime);
                setFormData(data);
                setOriginalData(data);
                setIsLoading(false);
            }
        } catch (error) {
            throw (error);
        }
    };

    const fetchActiveCourse = async () => {
        setIsLoading(true);
        try {
            const res = localStorage.getItem("courseData")
                ? JSON.parse(localStorage.getItem("courseData"))
                : null;
            setCourseData(res);
            const settingRes = await getTransportSettings(res.id);
            setTransportationSetting(settingRes.data);
        } catch (error) {
            throw (error);
        }
    };
    const init = async () => {
        setIsLoading(true);
        try {
            await fetchTransport();
            await fetchActiveCourse();
        } catch (error) {
            errorToast(error, t("general.Reload"), t);
        } finally {
            setIsLoading(false);
        }
    }
    useEffect(() => {
        init();
    }, [])

    useEffect(() => {
        setFormValidationSchema(["is_needed_arrive_shuttle_service", "is_needed_depart_shuttle_service"]);
        if (formData.is_needed_arrive_shuttle_service === "1") {
            setFormValidationSchema((prev) => [
                ...prev,
                "arrive_transport_mode", "arrive_datetime", "arrive_terminal",
            ])
            if (formData.arrive_transport_mode === "plane") {
                setFormValidationSchema((prev) => [
                    ...prev,
                    "arrive_airplane_no",
                ])
            }
        }
        if (formData.is_needed_depart_shuttle_service === "1") {
            setFormValidationSchema((prev) => [
                ...prev,
                "depart_transport_mode", "depart_datetime", "depart_terminal",
            ])
            if (formData.depart_transport_mode === "plane") {
                setFormValidationSchema((prev) => [
                    ...prev,
                    "depart_airplane_no",
                ])
            }
        }
    }, [formData])

    const handleEditing = () => {
        setIsEditing(true);
        setFormPercentage("33%");
        setFormSection(1);
    }

    const renderTransportRows = (startTime, endTime, type) => {
        const start = dayjs.utc(startTime);
        const end = dayjs.utc(endTime);

        // Calculate calendar day difference
        const diffDays = end.startOf('day').diff(start.startOf('day'), 'day');
        const rows = [];

        // Row 1: The Start Day
        // Pickup usually starts at a dynamic time (H:mm), 
        // Dropoff usually starts at 18:00 based on your code.
        const startRange = type === 'pickup' ? `${start.format("H:mm")}-23:59` : `18:00-23:59 ${t("ctransportation.dropoff2")}`;

        rows.push(
            lang === "en"
                ? `${start.format("D MMMM (dddd)")}: ${startRange}`
                : `${start.locale("zh-cn").format("M月D日 (dddd)")}: ${startRange}`
        );

        // Row 2: Middle Days (Full 24h)
        if (diffDays >= 2) {
            for (let i = 1; i < diffDays; i++) {
                const middleDay = start.add(i, 'day');
                rows.push(
                    lang === "en"
                        ? `${middleDay.format("D MMMM (dddd)")}: 0:00-23:59`
                        : `${middleDay.locale("zh-cn").format("M月D日 (dddd)")}: 0:00-23:59`
                );
            }
        }

        // Row 3: The End Day
        // Pickup ends at a specific time (H:mm), 
        // Dropoff ends at the end of the day (23:59) based on your code.
        const endRange = `0:00-${end.format("H:mm")}`;

        if (diffDays >= 1) {
            rows.push(
                lang === "en"
                    ? `${end.format("D MMMM (dddd)")}: ${endRange}`
                    : `${end.locale("zh-cn").format("M月D日 (dddd)")}: ${endRange}`
            );
        }

        return rows;
    };

    const onDateChange = (e) => {
        const { id, value } = e.target;

        setFormData((prevState) => ({
            ...prevState,
            [id]: value,
        }));
        validateField(id, value);

        const isValidArrivalDate = (value) =>
            new Date(value) >= new Date(
                dayjs.utc(transportationSetting.arrive_shuttle_start_time)
                    .startOf('day')
                    .format("YYYY-MM-DDTHH:mm")
            )
            &&
            new Date(value) <= new Date(convertToUTCDateTimeInputFormat(transportationSetting.arrive_shuttle_end_time));

        const isValidDepartureDate = (value) =>
            new Date(value) >= new Date(
                dayjs(transportationSetting.depart_shuttle_start_time)
                    .add(4, "hour")
                    .utc()
                    .format("YYYY-MM-DDTHH:mm")
            )
            &&
            new Date(value) <= new Date(
                dayjs.utc(transportationSetting.depart_shuttle_end_time)
                    .endOf('day')
                    .format("YYYY-MM-DDTHH:mm")
            );

        if (id === "arrive_datetime") {

            if (!isValidArrivalDate(value)) {
                setArriveDateError(
                    t("FormValid.arriveShuttleServiceError", {
                        start_date: lang === "en" ?
                            dayjs.utc(transportationSetting.arrive_shuttle_start_time).format("D (dddd)")
                            :
                            dayjs.utc(transportationSetting.arrive_shuttle_start_time)
                                .locale("zh-cn")
                                .format("YYYY年M月D日 (dddd)"),

                        end_datetime: lang === "en" ?
                            dayjs.utc(transportationSetting.arrive_shuttle_end_time).format("D MMMM YYYY (dddd) h:mm A")
                            :
                            dayjs.utc(transportationSetting.arrive_shuttle_end_time)
                                .locale("zh-cn")
                                .format("D日 (dddd) A h:mm")
                    })
                );
            } else {
                setArriveDateError(null)
            }

        } else {
            if (!isValidDepartureDate(value)) {
                setDepartDateError(
                    t("FormValid.departShuttleServiceError", {
                        start_date: lang === "en" ?
                            dayjs.utc(transportationSetting.depart_shuttle_start_time).format("h:mm A on D MMMM (dddd)")
                            :
                            dayjs.utc(transportationSetting.depart_shuttle_start_time)
                                .locale("zh-cn")
                                .format("YYYY年M月D日 (dddd) A h:mm"),

                        end_datetime: lang === "en" ?
                            dayjs.utc(transportationSetting.depart_shuttle_end_time).format("D MMMM YYYY (dddd) h:mm A")
                            :
                            dayjs.utc(transportationSetting.depart_shuttle_end_time)
                                .locale("zh-cn")
                                .format("M月D日 (dddd) A h:mm"),
                        start_datetime: lang === "en" ?
                            dayjs.utc(transportationSetting.depart_shuttle_start_time).add(4, "hour").format("D MMMM YYYY (dddd) h:mm A")
                            :
                            dayjs.utc(transportationSetting.depart_shuttle_start_time)
                                .add(4, "hour")
                                .locale("zh-cn")
                                .format("M月D日 (dddd) A h:mm")
                    })
                );
            } else {
                setDepartDateError(null)
            }
        }
    };

    const handleInputChange = (e) => {
        const { id, value, name } = e.target;

        const handleRadioInputChange = (fields, label) => {
            if (id === label) {
                setFormData((prevState) => {
                    const updatedState = { ...prevState, [name]: id };
                    fields.forEach((field) => {
                        updatedState[field] = "";
                    });
                    return updatedState;
                });

                if (label === "0") {
                    if (name === "is_needed_arrive_shuttle_service") {
                        setArriveDateError(null);
                    } else if (name === "is_needed_depart_shuttle_service") {
                        setDepartDateError(null);
                    }
                }
            } else {
                setFormData((prevState) => ({
                    ...prevState,
                    [name]: id
                }));
            }
            validateField(name, id);
        }
        if (name === "is_needed_arrive_shuttle_service") {
            const fields = ["arrive_transport_mode", "arrive_datetime", "arrive_terminal", "arrive_airplane_no", "arrive_ticket"];
            setPreviews((prev) => ({
                ...prev,
                'arrive_ticket': null,
            }));
            setFileError((prev) => ({
                ...prev,
                'arrive_ticket': null,
            }));

            handleRadioInputChange(fields, "0");
        } else if (name === "arrive_transport_mode") {
            const fields = ["arrive_airplane_no", "arrive_ticket"];
            setPreviews((prev) => ({
                ...prev,
                'arrive_ticket': null,
            }));
            setFileError((prev) => ({
                ...prev,
                'arrive_ticket': null,
            }));
            handleRadioInputChange(fields, "other");
        } else if (name === "is_needed_depart_shuttle_service") {
            const fields = ["depart_transport_mode", "depart_datetime", "depart_terminal", "depart_airplane_no", "depart_ticket"];
            handleRadioInputChange(fields, "0");
            setPreviews((prev) => ({
                ...prev,
                'depart_ticket': null,
            }));
            setFileError((prev) => ({
                ...prev,
                'depart_ticket': null,
            }));
        } else if (name === "depart_transport_mode") {
            const fields = ["depart_airplane_no", "depart_ticket"];
            handleRadioInputChange(fields, "other");
            setPreviews((prev) => ({
                ...prev,
                'depart_ticket': null,
            }));
            setFileError((prev) => ({
                ...prev,
                'depart_ticket': null,
            }));
        } else {
            setFormData((prevState) => ({
                ...prevState,
                [id]: value,
            }));
            validateField(id, value);
        }
    };

    const navigateFormSection = (forward) => {
        if (formSection === 2 && forward) {
            if (validateAllFields(formData, formValidationSchema) && !departDateError && !arriveDateError) {
                const section = forward ? formSection + 1 : formSection - 1;
                setFormSection(section);
                const percentage = (section / 3) * 100;
                const percentageString = percentage + "%";
                setFormPercentage(percentageString)
            } else {
                toast.error(t("general.ValidationFail"));
            }
        } else {
            const section = forward ? formSection + 1 : formSection - 1;
            setFormSection(section);
            const percentage = (section / 3) * 100;
            const percentageString = percentage + "%";
            setFormPercentage(percentageString)
        }
    }
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [formSection]);

    const handleCancel = () => {
        setIsEditing(false);
        setFormData(originalData);
    };

    const handleSubmit = () => {

        const submitTransportForm = async () => {
            try {
                const data = await updateTransportForm(formData);
                if (data) {
                    toast.success(t("ctransportation.FormSuccess"));
                    setTimeout(() => {
                        window.location.reload();
                    }, 1500);
                }
            } catch (error) {
                errorToast(error, t("personal_info.UpdateFailed"), t);
            } finally {
                setIsSubmitting(false);
            }
        }
        setIsSubmitting(true);
        if (validateAllFields(formData, formValidationSchema) && !departDateError && !arriveDateError) {
            submitTransportForm()
        } else {
            toast.error(t("personal_info.ValidateFailedfield"));
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {(originalData === null || courseData === null || transportationSetting === null) ?
                <div className='info-container'>
                    <h3>{t("ctransportation.title")}</h3>
                    {isLoading ?
                        <>
                            <AiOutlineLoading className='spin' />
                            <p>{t("ctransportation.LoadingTrans")}</p>
                        </>
                        :
                        <>
                            <p style={{ "color": "#ff0000" }}>
                                {t("ctransportation.EmptyTransfer")}
                            </p>
                            <div className='action-buttons'>
                                <Link to={`${base_url}/retreat-transportation`}>
                                    <Button
                                        buttonStyle='btn--primary'
                                        buttonSize='btn--small'
                                    >{t("ctransportation.SubmitTRANSPORT")}</Button>
                                </Link>

                            </div>
                        </>
                    }

                </div>
                :
                <>
                    {isEditing ?
                        <>
                            <div className='info-container'>
                                <h3>{t("ctransportation.title")}</h3>
                                <p>{lang === 'en' ? courseData.course_ename : courseData.course_cname}</p>
                                <div className='action-buttons'>
                                    <Button
                                        buttonStyle='btn--primary'
                                        buttonSize='btn--small'
                                        onClick={handleCancel}
                                    >{t("ctransportation.cancel")}</Button>
                                </div>
                            </div>
                            <div className='form-wrapper'>
                                <h4 className='d-flex mt-3 justify-content-left'>{t("ctransportation.step")} {formSection} {t("ctransportation.of_3")}</h4>
                                <div className="progress mb-5">
                                    <div className="progress-bar bg-success" role="progressbar" style={{ "width": formPercentage }} aria-valuenow="25" aria-valuemin="0" aria-valuemax="100"></div>
                                </div>
                                <form>
                                    <div className='form-section-1' hidden={formSection !== 1}>
                                        <p>
                                            <Trans key="ctransportation" html="br/:<br />">
                                                {t("ctransportation.student")}
                                            </Trans>
                                        </p>
                                        <p>
                                            {t("ctransportation.airportdes1")}
                                        </p>
                                        <p>
                                            <strong> 🛬{t("ctransportation.klia1")}</strong>
                                            <br />
                                            <strong> {t("ctransportation.sklia1")}</strong>
                                            <br />
                                        </p>
                                        <p>
                                            <strong>🛬 {t("ctransportation.klia2")}</strong>
                                            <br />
                                            <strong> {t("ctransportation.sklia2")}</strong>
                                            <br />
                                        </p>
                                        <p>
                                            {t("ctransportation.ownarrange")}
                                        </p>
                                        <p>
                                            {t("ctransportation.confirm")}
                                        </p>
                                        <p>
                                            {t("ctransportation.gmt")}
                                        </p>
                                        <p>
                                            {t("ctransportation.cost")}
                                        </p>

                                        <p><strong>{t("ctransportation.schedule_title")}</strong></p>
                                        <p>
                                            <strong>{t("ctransportation.pickup")}</strong>
                                            {renderTransportRows(
                                                transportationSetting.arrive_shuttle_start_time,
                                                transportationSetting.arrive_shuttle_end_time,
                                                'pickup'
                                            ).map((row, i) => <div key={i}>{row}</div>)}
                                        </p>
                                        <p>
                                            <strong>{t("ctransportation.dropoff")}</strong>
                                            {renderTransportRows(
                                                transportationSetting.depart_shuttle_start_time,
                                                transportationSetting.depart_shuttle_end_time,
                                                'dropoff'
                                            ).map((row, i) => <div key={i}>{row}</div>)}
                                        </p>
                                        <p>{t("ctransportation.deadline")}</p>
                                        {/* <p>{t("ctransportation.deadline2")}</p> */}
                                    </div>
                                    <div className='form-section-2' hidden={formSection !== 2}>
                                        <div className='section-title'>
                                            <h4 className='mb-0'>{t("ctransportation.arrival")}</h4>
                                        </div>
                                        <div className="form-item">
                                            <label>{t("ctransportation.arrivalq")}
                                                <span style={{ "color": "#ff0000" }}>*</span></label>
                                            <div className='radio'>
                                                <input className="form-check-input" type="radio" name="is_needed_arrive_shuttle_service" id="1" required
                                                    onChange={handleInputChange} disabled={!isEditing} checked={formData.is_needed_arrive_shuttle_service === "1"} />
                                                <p className='radioLabel mb-0'>{t("ctransportation.yes")}</p>
                                            </div>
                                            <div className='radio'>
                                                <input className="form-check-input" type="radio" name="is_needed_arrive_shuttle_service" id="0" required
                                                    onChange={handleInputChange} disabled={!isEditing} checked={formData.is_needed_arrive_shuttle_service === "0"} />
                                                <p className='radioLabel mb-0'>{t("ctransportation.no")}</p>
                                            </div>
                                        </div>
                                        {errors.is_needed_arrive_shuttle_service && <span className="error">{errors.is_needed_arrive_shuttle_service}</span>}
                                        {formData.is_needed_arrive_shuttle_service === "1" && (
                                            <>
                                                <div className="form-item">
                                                    <label>{t("ctransportation.arr_mode")}
                                                        <span style={{ "color": "#ff0000" }}>*</span></label>
                                                    <div className='radio'>
                                                        <input className="form-check-input" type="radio" name="arrive_transport_mode" id="plane" required
                                                            onChange={handleInputChange} disabled={!isEditing} checked={formData.arrive_transport_mode === "plane"} />
                                                        <p className='radioLabel mb-0'>{t("ctransportation.plane")}</p>
                                                    </div>
                                                    <div className='radio'>
                                                        <input className="form-check-input" type="radio" name="arrive_transport_mode" id="other" required
                                                            onChange={handleInputChange} disabled={!isEditing} checked={formData.arrive_transport_mode === "other"} />
                                                        <p className='radioLabel mb-0'>{t("ctransportation.train")}</p>
                                                    </div>
                                                </div>
                                                {errors.arrive_transport_mode && <span className="error">{errors.arrive_transport_mode}</span>}
                                                <div className="form-item">
                                                    <label className="form-label">{t("ctransportation.arr_time")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                                    <input
                                                        type='datetime-local'
                                                        id="arrive_datetime"
                                                        onChange={onDateChange}
                                                        value={
                                                            formData.arrive_datetime || null
                                                        }
                                                        min={
                                                            dayjs.utc(transportationSetting.arrive_shuttle_start_time)
                                                                .startOf('day') // Forces time to 00:00
                                                                .format("YYYY-MM-DDTHH:mm")
                                                        }
                                                        max={
                                                            dayjs.utc(transportationSetting.arrive_shuttle_end_time)
                                                                .endOf('day') // Forces time to 00:00
                                                                .format("YYYY-MM-DDTHH:mm")
                                                        }
                                                    />
                                                </div>
                                                <div className="form-info mt-2">
                                                    <p className='m-0'>{t("ctransportation.check1")}</p>
                                                </div>
                                                {errors.arrive_datetime && <span className="error">{errors.arrive_datetime}</span>}
                                                {arriveDateError && <span className="error">{arriveDateError}</span>}
                                                <div className="form-item">
                                                    <label for='arrival-airport'>{t("ctransportation.arr_airport")}
                                                        <span style={{ "color": "#ff0000" }}>*</span></label>
                                                    <select className="form-select" id='arrive_terminal'
                                                        onChange={handleInputChange}
                                                        disabled={!isEditing}
                                                        value={formData.arrive_terminal || ""}
                                                    >
                                                        <option value="">{t("ctransportation.PICKUPdes")}</option>
                                                        <option value="KLIA1">{t("ctransportation.klt1")}</option>
                                                        <option value="KLIA2">{t("ctransportation.klt2")}</option>
                                                    </select>
                                                </div>
                                                {errors.arrive_terminal && <span className="error">{errors.arrive_terminal}</span>}
                                                <div className="form-info mt-2">
                                                    <p className='m-0'>{t("ctransportation.checkArrivalAirport")}</p>
                                                </div>
                                                {formData.arrive_transport_mode === "plane" && (
                                                    <>
                                                        <div className="form-item">
                                                            <label for="arrival-flight" className="form-label">{t("ctransportation.arr_number")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                                            <input type="text" className="form-control" id="arrive_airplane_no" placeholder="" onChange={handleInputChange} disabled={!isEditing} value={formData.arrive_airplane_no || ""} required />
                                                        </div>
                                                        {errors.arrive_airplane_no && <span className="error">{errors.arrive_airplane_no}</span>}
                                                        <div className="form-item">
                                                            <label for="arrive_ticket" className="form-label">
                                                                {t("ctransportation.upload")}
                                                                <span style={{ "color": "#ff0000" }}>*</span>
                                                            </label>
                                                            {
                                                                previews.arrive_ticket ?
                                                                    <img src={previews.arrive_ticket} className="img-thumbnail mt-3" style={{ maxWidth: "200px" }} />
                                                                    :
                                                                    <img src={`${originalData.arrive_ticket}?_=${Date.now()}`} alt="" style={{ "width": "200px" }} />
                                                            }
                                                            <input
                                                                type="file"
                                                                className="form-control mt-2"
                                                                onChange={handleFileChange}
                                                                disabled={!isEditing}
                                                                id="arrive_ticket"
                                                            />
                                                        </div>
                                                        <div className="form-info mt-2">
                                                            <p className='mb-0'>{t("general.smallerFile10MB")}</p>
                                                        </div>
                                                        {fileError?.arrive_ticket && <div className="error mt-2">{fileError?.arrive_ticket}</div>}
                                                        {errors.arrive_ticket && <span className="error">{errors.arrive_ticket}</span>}
                                                    </>
                                                )}
                                            </>
                                        )}
                                        <div className="section-title mt-3">
                                            <h4 className='mb-0'>{t("ctransportation.departure")}</h4>
                                        </div>
                                        <div className="form-item">
                                            <label>{t("ctransportation.departq")}
                                                <span style={{ "color": "#ff0000" }}>*</span></label>
                                            <div className='radio'>
                                                <input className="form-check-input" type="radio" name="is_needed_depart_shuttle_service" id="1" required
                                                    onChange={handleInputChange} disabled={!isEditing} checked={formData.is_needed_depart_shuttle_service === "1"} />
                                                <p className='radioLabel mb-0'>{t("ctransportation.yes")}</p>
                                            </div>
                                            <div className='radio'>
                                                <input className="form-check-input" type="radio" name="is_needed_depart_shuttle_service" id="0" required
                                                    onChange={handleInputChange} disabled={!isEditing} checked={formData.is_needed_depart_shuttle_service === "0"} />
                                                <p className='radioLabel mb-0'>{t("ctransportation.no")}</p>
                                            </div>
                                        </div>
                                        {errors.is_needed_depart_shuttle_service && <span className="error">{errors.is_needed_depart_shuttle_service}</span>}
                                        {formData.is_needed_depart_shuttle_service === "1" && (
                                            <>
                                                <div className="form-item">
                                                    <label>{t("ctransportation.dep_mode")}
                                                        <span style={{ "color": "#ff0000" }}>*</span></label>
                                                    <div className='radio'>
                                                        <input className="form-check-input" type="radio" name="depart_transport_mode" id="plane" required
                                                            onChange={handleInputChange} disabled={!isEditing} checked={formData.depart_transport_mode === "plane"} />
                                                        <p className='radioLabel mb-0'>{t("ctransportation.plane")}</p>
                                                    </div>
                                                    <div className='radio'>
                                                        <input className="form-check-input" type="radio" name="depart_transport_mode" id="other" required
                                                            onChange={handleInputChange} disabled={!isEditing} checked={formData.depart_transport_mode === "other"} />
                                                        <p className='radioLabel mb-0'>{t("ctransportation.train")}</p>
                                                    </div>
                                                </div>
                                                {errors.depart_transport_mode && <span className="error">{errors.depart_transport_mode}</span>}
                                                <div className="form-item">
                                                    <label className="form-label">{t("ctransportation.dep_time")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                                    <input
                                                        type='datetime-local'
                                                        id="depart_datetime"
                                                        onChange={onDateChange}
                                                        value={
                                                            formData.depart_datetime || null
                                                        }
                                                        min={
                                                            dayjs.utc(transportationSetting.depart_shuttle_start_time)
                                                                .startOf('day') // Forces time to 00:00
                                                                .format("YYYY-MM-DDTHH:mm")
                                                        }
                                                        max={
                                                            dayjs.utc(transportationSetting.depart_shuttle_end_time)
                                                                .endOf('day') // Forces time to 00:00
                                                                .format("YYYY-MM-DDTHH:mm")
                                                        }
                                                    />
                                                </div>
                                                <div className="form-info mt-2">
                                                    <p className='m-0'>{t("ctransportation.check2")}</p>
                                                </div>
                                                {errors.depart_datetime && <span className="error">{errors.depart_datetime}</span>}
                                                {departDateError && <span className="error">{departDateError}</span>}
                                                <div className="form-item">
                                                    <label for='departure-airport'>{t("ctransportation.dep_airport")}
                                                        <span style={{ "color": "#ff0000" }}>*</span></label>
                                                    <select className="form-select" id='depart_terminal' onChange={handleInputChange} disabled={!isEditing} value={formData.depart_terminal}>
                                                        <option value="">{t("ctransportation.DROPOFFdes")}</option>
                                                        <option value="KLIA1">{t("ctransportation.klt1")}</option>
                                                        <option value="KLIA2">{t("ctransportation.klt2")}</option>
                                                    </select>
                                                </div>
                                                {errors.depart_terminal && <span className="error">{errors.depart_terminal}</span>}
                                                <div className="form-info mt-2">
                                                    <p className='m-0'>{t("ctransportation.checkDepartureAirport")}</p>
                                                </div>
                                                {formData.depart_transport_mode === "plane" && (
                                                    <>
                                                        <div className="form-item">
                                                            <label for="depart_airplane_no" className="form-label">{t("ctransportation.dep_number")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                                            <input type="text" className="form-control" id="depart_airplane_no" onChange={handleInputChange} disabled={!isEditing} value={formData.depart_airplane_no} required />
                                                        </div>
                                                        {errors.depart_airplane_no && <span className="error">{errors.depart_airplane_no}</span>}
                                                        <div className="form-item">
                                                            <label for="depart_ticket" className="form-label">
                                                                {t("ctransportation.upload2")}
                                                                <span style={{ "color": "#ff0000" }}>*</span>
                                                            </label>
                                                            {
                                                                previews.depart_ticket ?
                                                                    <img src={previews.depart_ticket} className="img-thumbnail mt-3" style={{ maxWidth: "200px" }} />
                                                                    :
                                                                    <img src={`${originalData.depart_ticket}?_=${Date.now()}`} alt="" style={{ "width": "200px" }} />
                                                            }
                                                            <input
                                                                type="file"
                                                                className="form-control mt-2"
                                                                onChange={handleFileChange}
                                                                id="depart_ticket"
                                                                disabled={!isEditing}
                                                            />
                                                        </div>
                                                        <div className="form-info mt-2">
                                                            <p className='mb-0'>{t("general.smallerFile")}</p>
                                                        </div>
                                                        {fileError?.depart_ticket && <div className="error mt-2">{fileError?.depart_ticket}</div>}
                                                        {errors.depart_ticket && <span className="error">{errors.depart_ticket}</span>}
                                                    </>
                                                )}
                                            </>
                                        )}

                                    </div>
                                    <div className='form-section-3' hidden={formSection !== 3}>
                                        <SubmissionConfirmationPage />
                                    </div>
                                    <div className='button-container mt-3'>
                                        <Button buttonStyle="btn--primary" hidden={formSection === 1} type="button" onClick={() => navigateFormSection(false)}>{t("general.BTNprevious")}</Button>
                                        <Button buttonStyle="btn--primary" hidden={formSection === 3} type="button" onClick={() => navigateFormSection(true)}>{t("general.BTNnext")}</Button>
                                        <Button buttonStyle="btn--primary" hidden={formSection !== 3} type="button" onClick={() => handleSubmit()}>
                                            {isSubmitting ? <AiOutlineLoading className="spin" /> : t("general.BTNsubmit")}
                                        </Button>
                                    </div>

                                </form>
                            </div>
                        </>
                        :
                        <>
                            <div className='info-container'>
                                <h3>{t("ctransportation.title")}</h3>
                                <p>{lang === 'en' ? courseData.course_ename : courseData.course_cname}</p>
                            </div>
                            {originalData.is_needed_arrive_shuttle_service === "1" ||
                                originalData.is_needed_depart_shuttle_service === "1" ?
                                <div className='form-wrapper mt-3'>
                                    <h3 className='mt-2'>{t("ctransportation.ShuttleInfo")}</h3>
                                    <p>{t("ctransportation.ShuttleAssign")}
                                        <span className='error'> {t("ctransportation.GrpNum")}</span> {t("ctransportation.Punctual")}</p>
                                    <table className="table table-light">
                                        <thead>
                                            <tr>
                                                <th scope="col"></th>
                                                {/* <th scope="col">{t("ctransportation.ShuttleGrp")}</th> */}
                                                <th scope="col">{t("ctransportation.ShuttleTime")}</th>
                                                <th scope="col">{t("ctransportation.ShuttleVenue")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {originalData.is_needed_arrive_shuttle_service === "1" &&
                                                <tr>
                                                    <th>
                                                        {t("ctransportation.PickupService")}
                                                        <br />{t("ctransportation.ShuttleGrp")}:
                                                        ({originalData.arrive_shuttle_group || t("interview.DecideYET")})
                                                    </th>
                                                    {/* <td>{originalData.arrive_shuttle_group || t("interview.DecideYET")}</td> */}
                                                    <td>{originalData.arrive_shuttle_datetime ?
                                                        dayjs.utc(originalData.arrive_shuttle_datetime).format("YYYY-MM-DD hh:mmA") :
                                                        t("interview.DecideYET")}</td>
                                                    <td>{originalData.arrive_shuttle_terminal || t("interview.DecideYET")}</td>
                                                </tr>
                                            }
                                            {originalData.is_needed_depart_shuttle_service === "1" &&

                                                <tr>
                                                    <th>
                                                        {t("ctransportation.DropOffService")}
                                                        <br />{t("ctransportation.ShuttleGrp")}:
                                                        ({originalData.depart_shuttle_group || t("interview.DecideYET")})
                                                    </th>
                                                    {/* <td>{originalData.depart_shuttle_group || t("interview.DecideYET")}</td> */}
                                                    <td>{originalData.depart_shuttle_datetime ? dayjs.utc(originalData.depart_shuttle_datetime).format("YYYY-MM-DD hh:mmA") : t("interview.DecideYET")}</td>
                                                    <td>{originalData.depart_shuttle_terminal || t("interview.DecideYET")}</td>
                                                </tr>
                                            }
                                        </tbody>
                                    </table>
                                </div> :
                                <></>
                            }
                            <div className='form-wrapper'>
                                <div className='action-buttons d-flex justify-content-center'>
                                    <Button
                                        buttonStyle='btn--primary'
                                        buttonSize='btn--small'
                                        onClick={handleEditing}
                                    >{t("general.BTNedit")}</Button>
                                </div>
                                <div className='display-section'>
                                    <div className='section-title mt-3'>
                                        <h4 className='mb-0'>{t("ctransportation.arrival")}</h4>
                                    </div>
                                    <div className="form-item">
                                        <label>{t("ctransportation.arrivalq")}
                                            <span style={{ "color": "#ff0000" }}>*</span></label>
                                        <div className='radio'>
                                            <input className="form-check-input" type="radio" name="is_needed_arrive_shuttle_service" id="1" required
                                                checked={originalData.is_needed_arrive_shuttle_service === "1"} disabled />
                                            <p className='radioLabel mb-0'>{t("ctransportation.yes")}</p>
                                        </div>
                                        <div className='radio'>
                                            <input className="form-check-input" type="radio" name="is_needed_arrive_shuttle_service" id="0" required
                                                checked={originalData.is_needed_arrive_shuttle_service === "0"} disabled />
                                            <p className='radioLabel mb-0'>{t("ctransportation.no")}</p>
                                        </div>
                                    </div>
                                    {formData.is_needed_arrive_shuttle_service === "1" && (
                                        <>
                                            <div className="form-item">
                                                <label>{t("ctransportation.arr_mode")}
                                                    <span style={{ "color": "#ff0000" }}>*</span></label>
                                                <div className='radio'>
                                                    <input className="form-check-input" type="radio" name="arrive_transport_mode" id="plane" required
                                                        checked={originalData.arrive_transport_mode === "plane"} disabled />
                                                    <p className='radioLabel mb-0'>{t("ctransportation.plane")}</p>
                                                </div>
                                                <div className='radio'>
                                                    <input className="form-check-input" type="radio" name="arrive_transport_mode" id="other" required
                                                        checked={originalData.arrive_transport_mode === "other"} disabled />
                                                    <p className='radioLabel mb-0'>{t("ctransportation.train")}</p>
                                                </div>
                                            </div>
                                            <div className="form-item">
                                                <label className="form-label">{t("ctransportation.arr_time")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                                <input
                                                    type='datetime-local'
                                                    id="arrive_datetime"
                                                    value={convertToDateTimeInputFormat(originalData.arrive_datetime) || null}
                                                    disabled
                                                />
                                            </div>
                                            <div className="form-info mt-2">
                                                <p className='mb-0'>{t("ctransportation.check1")}</p>
                                            </div>
                                            <div className="form-item">
                                                <label for='arrival-airport'>{t("ctransportation.arr_airport")}
                                                    <span style={{ "color": "#ff0000" }}>*</span></label>
                                                <select className="form-select" id='arrive_terminal'
                                                    disabled
                                                    value={originalData.arrive_terminal || ""}
                                                >
                                                    <option value="">{t("ctransportation.PICKUPdes")}</option>
                                                    <option value="KLIA1">{t("ctransportation.klt1")}</option>
                                                    <option value="KLIA2">{t("ctransportation.klt2")}</option>
                                                </select>
                                            </div>
                                            <div className="form-info mt-2">
                                                <p className='m-0'>{t("ctransportation.checkArrivalAirport")}</p>
                                            </div>
                                            {formData.arrive_transport_mode === "plane" && (
                                                <>
                                                    <div className="form-item">
                                                        <label for="arrival-flight" className="form-label">{t("ctransportation.arr_number")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                                        <input type="text" className="form-control" id="arrive_airplane_no" placeholder="" value={originalData.arrive_airplane_no || ""} disabled />
                                                    </div>
                                                    <label className="form-label">{t("ctransportation.uploadedArriveTicketLabel")}</label>
                                                    <img src={`${originalData.arrive_ticket}?_=${Date.now()}`} alt="" style={{ "width": "200px" }} />
                                                </>
                                            )}
                                        </>
                                    )}
                                    <div className="section-title mt-5">
                                        <h4 className='mb-0'>{t("ctransportation.departure")}</h4>
                                    </div>
                                    <div className="form-item">
                                        <label>{t("ctransportation.departq")}
                                            <span style={{ "color": "#ff0000" }}>*</span></label>
                                        <div className='radio'>
                                            <input className="form-check-input" type="radio" name="is_needed_depart_shuttle_service" id="1" disabled
                                                checked={originalData.is_needed_depart_shuttle_service === "1"} />
                                            <p className='radioLabel mb-0'>{t("ctransportation.yes")}</p>
                                        </div>
                                        <div className='radio'>
                                            <input className="form-check-input" type="radio" name="is_needed_depart_shuttle_service" id="0" disabled
                                                checked={originalData.is_needed_depart_shuttle_service === "0"} />
                                            <p className='radioLabel mb-0'>{t("ctransportation.no")}</p>
                                        </div>
                                    </div>
                                    {formData.is_needed_depart_shuttle_service === "1" && (
                                        <>
                                            <div className="form-item">
                                                <label>{t("ctransportation.dep_mode")}
                                                    <span style={{ "color": "#ff0000" }}>*</span></label>
                                                <div className='radio'>
                                                    <input className="form-check-input" type="radio" name="depart_transport_mode" id="plane"
                                                        disabled checked={originalData.depart_transport_mode === "plane"} />
                                                    <p className='radioLabel mb-0'>{t("ctransportation.plane")}</p>
                                                </div>
                                                <div className='radio'>
                                                    <input className="form-check-input" type="radio" name="depart_transport_mode" id="other"
                                                        disabled checked={originalData.depart_transport_mode === "other"} />
                                                    <p className='radioLabel mb-0'>{t("ctransportation.train")}</p>
                                                </div>
                                            </div>
                                            <div className="form-item">
                                                <label className="form-label">{t("ctransportation.dep_time")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                                <input
                                                    type='datetime-local'
                                                    id="depart_datetime"
                                                    value={convertToDateTimeInputFormat(originalData.depart_datetime) || null}
                                                    disabled
                                                />
                                            </div>
                                            <div className="form-info mt-2">
                                                <p className='mb-0'>{t("ctransportation.check2")}</p>
                                            </div>
                                            <div className="form-item">
                                                <label for='departure-airport'>{t("ctransportation.dep_airport")}
                                                    <span style={{ "color": "#ff0000" }}>*</span></label>
                                                <select className="form-select" id='depart_terminal' disabled value={originalData.depart_terminal}>
                                                    <option value="">{t("ctransportation.DROPOFFdes")}</option>
                                                    <option value="KLIA1">{t("ctransportation.klt1")}</option>
                                                    <option value="KLIA2">{t("ctransportation.klt2")}</option>
                                                </select>
                                            </div>
                                            <div className="form-info mt-2">
                                                <p className='m-0'>{t("ctransportation.checkDepartureAirport")}</p>
                                            </div>
                                            {formData.depart_transport_mode === "plane" && (
                                                <>
                                                    <div className="form-item">
                                                        <label for="depart_airplane_no" className="form-label">{t("ctransportation.dep_number")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                                        <input type="text" className="form-control" id="depart_airplane_no" disabled value={originalData.depart_airplane_no} />
                                                    </div>
                                                    <label className="form-label">{t("ctransportation.uploadedDepartTicketLabel")}</label>
                                                    <img src={`${originalData.depart_ticket}?_=${Date.now()}`} alt="" style={{ "width": "200px" }} />
                                                </>
                                            )}
                                        </>
                                    )}

                                </div>

                            </div>
                        </>
                    }
                </>

            }
        </>
    )
}

export default ViewTransportation